import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Tooltip,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import { PaymentHistoryItem, PaynowService } from '../../api/paynowService';
import PaymentStatusChip from './PaymentStatusChip';
import { isAuthenticated } from '../../utils/auth';
import { api } from '../../api/client';

interface PaymentHistoryTableProps {
  loanId?: number; // If provided, shows history for specific loan
  title?: string;
}

const PaymentHistoryTable: React.FC<PaymentHistoryTableProps> = ({
  loanId,
  title = 'Payment History'
}) => {
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const mapBackendPaymentToItem = (p: any, fallbackLoanId?: number): PaymentHistoryItem => {
    const rawType = p?.paymentType;
    const typeStr = typeof rawType === 'string' ? rawType : (rawType?.toString?.() ?? 'LOAN_REPAYMENT');
    return {
      id: p.id,
      paymentId: p.paymentReference || String(p.id),
      paynowReference: p.paynowReference || '',
      loanId: p.fineractLoanId || p.clientLoanId || fallbackLoanId || 0,
      paymentType: typeStr,
      amount: Number(p.amount || 0),
      status: p.paymentStatus || 'PENDING',
      reference: p.paymentReference || '',
      paynowStatus: p.paymentStatus || 'PENDING',
      createdAt: p.initiatedAt || p.createdAt || new Date().toISOString(),
      updatedAt: p.completedAt || p.updatedAt || p.initiatedAt || new Date().toISOString(),
    } as PaymentHistoryItem;
  };

  const fetchPayments = async (pageNum: number = 0) => {
    // Check authentication before making any API calls
    if (!isAuthenticated()) {

      return;
    }
    
    try {
      setLoading(true);
      setError('');

      let response: any;
      if (loanId) {
        // Strategy 1: Get ALL local payments for this loan (any status) via user-scoped history
        try {
          const allHistory = await PaynowService.getPaymentHistory(0, 100);


          
          const filtered = (Array.isArray(allHistory?.content) ? allHistory.content : [])
            .filter((p: any) => {
              const fineractLoanId = p?.fineractLoanId;
              const clientLoanId = p?.clientLoanId;
              const targetLoanId = Number(loanId);
              
              return Number(fineractLoanId) === targetLoanId || Number(clientLoanId) === targetLoanId;
            })
            .map((p: any) => mapBackendPaymentToItem(p, loanId));
            

          
          if (filtered.length > 0) {
            response = {
              content: filtered,
              totalPages: 1,
              totalElements: filtered.length,
              number: 0
            };
          }
        } catch (e) {

        }

        // Strategy 2: If no local payments found, try successful payments only
        if (!response || response.content.length === 0) {
          try {
            const localPayments = await PaynowService.getLocalSuccessfulPaymentsForLoan(loanId);
            if (localPayments.length > 0) {
              response = {
                content: localPayments,
                totalPages: 1,
                totalElements: localPayments.length,
                number: 0
              };
            }
          } catch (e) {

          }
        }

        // Strategy 2.5: If still no payments, show ALL user payments (fallback for debugging)
        if (!response || response.content.length === 0) {
          try {
            const allHistory = await PaynowService.getPaymentHistory(0, 100);

            if (allHistory?.content && allHistory.content.length > 0) {
              response = {
                content: allHistory.content.map((p: any) => mapBackendPaymentToItem(p, loanId)),
                totalPages: 1,
                totalElements: allHistory.content.length,
                number: 0
              };
            }
          } catch (e) {

          }
        }

        // Strategy 2.6: Add Fineract transactions to local payments (combine both sources)
        if (response && response.content.length > 0) {
          try {
            const transactionsRes = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
            let transactions = transactionsRes.data.transactions || [];
            if (!Array.isArray(transactions) || transactions.length === 0) {
              if (Array.isArray(transactionsRes.data)) {
                transactions = transactionsRes.data;
              }
            }
            const repaymentTransactions = transactions.filter((tx: any) => {
              const description = (tx.description || '').toLowerCase();
              const typeValue = (tx.type?.value || '').toLowerCase();
              return typeValue.includes('repayment') || description.includes('repayment') || description.includes('loan');
            });
            const mappedFineract = repaymentTransactions.map((tx: any) => ({
              id: tx.id || 0,
              paymentId: tx.transactionId || tx.id || '',
              paynowReference: tx.referenceNumber || tx.reference || '',
              loanId: loanId,
              paymentType: tx.type?.value || 'Repayment',
              amount: tx.amount || 0,
              status: tx.manuallyReversed ? 'REVERSED' : 'PAID', // Changed from 'ACTIVE' to 'PAID'
              reference: tx.id?.toString() || tx.referenceNumber || tx.reference || '', // Use ID as reference
              paynowStatus: tx.manuallyReversed ? 'REVERSED' : 'PAID', // Changed from 'ACTIVE' to 'PAID'
              createdAt: tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toISOString() : (tx.transactionDate || tx.createdAt || new Date().toISOString()),
              updatedAt: tx.updatedAt || (tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toISOString() : new Date().toISOString())
            } as PaymentHistoryItem));
            
            // Combine local payments with Fineract transactions
            response.content = [...response.content, ...mappedFineract];
            response.totalElements = response.content.length;
          } catch (e) {

          }
        }

        // Strategy 3: Fallback to Fineract transactions if no local payments
        if (!response || response.content.length === 0) {
          try {
            const transactionsRes = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
            let transactions = transactionsRes.data.transactions || [];
            if (!Array.isArray(transactions) || transactions.length === 0) {
              if (Array.isArray(transactionsRes.data)) {
                transactions = transactionsRes.data;
              }
            }
            const repaymentTransactions = transactions.filter((tx: any) => {
              const description = (tx.description || '').toLowerCase();
              const typeValue = (tx.type?.value || '').toLowerCase();
              return typeValue.includes('repayment') || description.includes('repayment') || description.includes('loan');
            });
            const mapped = repaymentTransactions.map((tx: any) => ({
              id: tx.id || 0,
              paymentId: tx.transactionId || tx.id || '',
              paynowReference: tx.referenceNumber || tx.reference || '',
              loanId: loanId,
              paymentType: tx.type?.value || 'Repayment',
              amount: tx.amount || 0,
              status: tx.manuallyReversed ? 'REVERSED' : 'ACTIVE',
              reference: tx.referenceNumber || tx.reference || '',
              paynowStatus: tx.manuallyReversed ? 'REVERSED' : 'ACTIVE',
              createdAt: tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toISOString() : (tx.transactionDate || tx.createdAt || new Date().toISOString()),
              updatedAt: tx.updatedAt || (tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toISOString() : new Date().toISOString())
            } as PaymentHistoryItem));
            response = {
              content: mapped,
              totalPages: 1,
              totalElements: mapped.length,
              number: 0
            };
          } catch (loanErr: any) {
            console.error(`Failed to fetch transactions for loan ${loanId}:`, loanErr);
            setError('Failed to fetch loan transactions');
            return;
          }
        }
      } else {
        // Fetch all payment history from Paynow-backed endpoint
        response = await PaynowService.getPaymentHistory(pageNum, 10);
      }

      setPayments(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      setPage(response.number);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payment history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch payments if authenticated
    if (isAuthenticated()) {
      fetchPayments();
    }
  }, [loanId]);

  const handlePageChange = (_: any, newPage: number) => {
    // Check authentication before page change
    if (!isAuthenticated()) {

      sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
      window.location.href = '/login';
      return;
    }
    
    fetchPayments(newPage - 1);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
          {title}
          {totalElements > 0 && (
            <Chip 
              label={`${totalElements} payment${totalElements !== 1 ? 's' : ''}`}
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton 
            onClick={() => {
              // Check authentication before refresh
              if (!isAuthenticated()) {

                sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
                window.location.href = '/login';
                return;
              }
              fetchPayments(page);
            }} 
            disabled={loading}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {payments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PaymentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Payments Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loanId 
              ? 'No payments have been made for this loan yet.'
              : 'You haven\'t made any payments yet.'
            }
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Payment ID</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  {!loanId && <TableCell><strong>Loan</strong></TableCell>}
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Reference</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Tooltip title={payment.paynowReference}>
                        <Typography variant="body2" fontFamily="monospace">
                          {payment.paymentId}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PaynowService.formatPaymentType(payment.paymentType)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    {!loanId && (
                      <TableCell>
                        <Typography variant="body2">
                          {payment.loanId}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <PaymentStatusChip status={payment.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {payment.reference || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(payment.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                disabled={loading}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentHistoryTable;



